import React, { Dispatch, SetStateAction } from 'react';
import { Label } from '@/components/ui/label';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import { useGlobalCustomInputs } from '../hooks/useGlobalCustomInputs';
import type { CustomInput, FundraisingFormData } from '../../drawers/types/FundraisingPaymentTypes';
import type { Merchant } from '../../../types/common';

// Props interface for the CustomInputsInformation component
interface CustomInputsInformationProps {
  formData: FundraisingFormData;
  setFormData: Dispatch<SetStateAction<FundraisingFormData>>;
  merchant: Merchant | null;
  isEditMode: boolean;
  handleFieldFocus: (fieldName: string) => void;
  invalidFields: Set<string>;
}

export default function CustomInputsInformation({
  formData,
  setFormData,
  merchant,
  isEditMode,
  handleFieldFocus,
  invalidFields,
}: CustomInputsInformationProps): React.ReactElement {
  // Helper function to safely check if a field is invalid
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      return invalidFields.has(fieldName);
    }
    return false;
  };

  const { displayedGlobalCustomInputs, getGlobalCustomInputValue, updateGlobalCustomInputValue } =
    useGlobalCustomInputs<CustomInput>({
      globalCustomInputs: formData.globalCustomInputs,
      merchantGlobalCustomInputs: merchant?.globalCustomInputs as CustomInput[] | undefined,
      isEditMode,
      setGlobalCustomInputs: (updater) => {
        setFormData((prevData) => ({
          ...prevData,
          globalCustomInputs: updater(prevData.globalCustomInputs ?? []),
        }));
      },
    });

  return (
    <div>
      {/* Global Custom Inputs Section */}
      <div className="mt-2 space-y-3">
        {displayedGlobalCustomInputs.map((input: CustomInput, inputIndex: number) => {
          const inputValue = getGlobalCustomInputValue(input, inputIndex);

          return (
            <div key={inputIndex} className="space-y-0">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                {input.title?.trim?.()}
                {input.required && <span className="ml-1 text-red-500">*</span>}
                {!input.required && <span className="ml-1 text-ink-muted">(optional)</span>}
              </Label>

              {input.type === 'SINGLE_SELECTION_DROPDOWN' && (
                <select
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                      ${
                        isFieldInvalid(`global-custom-${inputIndex}`)
                          ? 'border-red-500'
                          : 'border-edge bg-white'
                      }
                  `}
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                >
                  <option value="">Select an option</option>
                  {input.options?.map((option: string, optIdx: number) => (
                    <option key={optIdx} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}

              {input.type === 'SHORT_TEXT_INPUT' && (
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                          ${
                            isFieldInvalid(`global-custom-${inputIndex}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          }
                      `}
                  placeholder="Enter your answer"
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                />
              )}

              {input.type === 'LONG_TEXT_INPUT' && (
                <textarea
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block min-h-[100px] w-full shrink-0 rounded-md border px-2 py-1.5 text-sm leading-tight
                          focus:border-sage focus:outline-none focus:ring-sage
                          ${
                            isFieldInvalid(`global-custom-${inputIndex}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          }
                      `}
                  rows={4}
                  placeholder="Enter your answer"
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                />
              )}

              {input.type === 'MULTI_SELECTION_DROPDOWN' && (
                <MultiSelectDropdownNoCreate
                  height={28}
                  options={
                    input.options?.map((option: string) => ({
                      value: option,
                      label: option,
                    })) || []
                  }
                  items={inputValue
                    .split(',')
                    .filter((v: string) => v.trim() !== '')}
                  setItems={(selectedValues: string[]) => {
                    updateGlobalCustomInputValue(inputIndex, input, selectedValues.join(','));
                  }}
                  error={isFieldInvalid(`global-custom-${inputIndex}`)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
